<template>
  <!-- Kundenfeedback 16.10.2026 Teil 2 (XXII): Die Meldung sitzt über dem
       schwebenden Knopf „Fehler melden“ (nicht dahinter), bricht lange Texte
       um (max. ~520 px) und lässt sich jederzeit per „×“ schließen. -->
  <v-snackbar
    v-model="isVisible"
    class="app-snackbar"
    :color="snackbarColor"
    :location="display.isMobile ? 'bottom center' : 'bottom right'"
    multi-line
    rounded="lg"
    :timeout="timeout"
    :vertical="isLong"
  >
    <div class="snackbar-progress-wrapper">
      <div class="snackbar-progress" :style="{ animationDuration: timeout + 'ms' }" />
    </div>
    <span class="snackbar-message" data-testid="app-snackbar-message"><SupportAwareText :text="snackbarMessage" @support="isVisible = false" /></span>
    <template #actions>
      <v-btn v-if="actionLabel" variant="text" @click="onActionClick">{{ actionLabel }}</v-btn>
      <v-btn
        :aria-label="$gt('Meldung schließen')"
        class="snackbar-close"
        data-testid="app-snackbar-close"
        icon
        size="small"
        variant="text"
        @click="isVisible = false"
      >
        <X :size="18" />
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import { X } from "lucide-vue-next";
import SupportAwareText from "@/components/app/SupportAwareText.vue";
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
import { useDisplayStore } from "@/stores/displayStore";

/** Ab dieser Länge wandern die Knöpfe unter den Text, damit er volle Breite bekommt. */
const LONG_MESSAGE_CHARS = 90;

export default {
  name: "Snackbar",

  components: { SupportAwareText, X },

  created() {
    this.display = useDisplayStore();
  },

  computed: {
    // Fehlermeldungen bleiben deutlich länger stehen als Erfolgsmeldungen —
    // 3 Sekunden waren zu kurz zum Lesen (Kundenfeedback 19.08.26, XII).
    // Snackbars mit Aktion (z. B. "Öffnen" bei neuer Nachricht) bleiben
    // ebenfalls länger stehen, damit der Klick realistisch möglich ist
    // (Kundenfeedback 29.08.26, III).
    timeout() {
      return this.snackbarColor === "error" || this.actionLabel ? 8000 : 3000;
    },

    isVisible: {
      get() {
        return useSnackbarStore().snackbar.visible;
      },

      set(value) {
        if (!value) {
          useSnackbarStore().hideSnackbar();
        }
      },
    },

    snackbarMessage() {
      return useSnackbarStore().snackbar.message;
    },

    isLong() {
      return String(this.snackbarMessage || "").length > LONG_MESSAGE_CHARS;
    },

    snackbarColor() {
      return useSnackbarStore().snackbar.color;
    },

    actionLabel() {
      return useSnackbarStore().snackbar.actionLabel;
    },
  },

  methods: {
    onActionClick() {
      useSnackbarStore().triggerAction();
    },
  },
};
</script>

<style scoped>
/* Platz über „Fehler melden“ (BugReportButton: 24 px Abstand + 44 px Höhe,
   plus --app-fab-lift bei fester Fußleiste). */
.app-snackbar :deep(.v-snackbar__wrapper) {
  max-width: min(520px, calc(100vw - 32px));
  margin-bottom: calc(80px + var(--app-fab-lift, 0px));
}
.app-snackbar :deep(.v-snackbar__content) {
  padding-right: 8px;
  white-space: pre-line;
  overflow-wrap: anywhere;
  line-height: 1.45;
}
.snackbar-message { display: block; }
.app-snackbar :deep(.v-snackbar__actions) {
  align-self: flex-start;
  margin-top: 4px;
}
.app-snackbar.v-snackbar--vertical :deep(.v-snackbar__actions) {
  align-self: flex-end;
  margin-top: 0;
}

.snackbar-progress-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  overflow: hidden;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.snackbar-progress {
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  animation-name: shrink;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes shrink {
  from {
    transform: scaleX(1);
    transform-origin: left;
  }
  to {
    transform: scaleX(0);
    transform-origin: left;
  }
}
</style>
